import React from 'react'
const { useState, useEffect, useCallback, useMemo, useRef } = React;

import {
  AirtableAPI, TABLE_IDS, CURRENT_USER, COMPANY_PROFILE,
  callOpenAI, navSetUrl, SOURCE_OPTIONS, OPP_STAGES, WON_STAGES, CLOSED_STAGES,
  BENCH_REPLY_HIGH, BENCH_REPLY_LOW, BENCH_MEETING_HIGH, BENCH_MEETING_LOW,
  CHANNEL_BENCHMARKS, MESSAGE_PROMPTS, MESSAGE_PROMPT_DEFAULTS, saveMessagePrompts,
  resolvePromptTemplate, saveCompanyProfile, channelIcon, logoutUser,
  CLIENT_CONFIG, AUTH_TOKEN, loadBranding, BRANDING_LS_KEY, getAuthHeaders, AIRTABLE_BASE_ID,
} from '../globals.js';
import {
  F, linkedIds, resolveLinked, InfoTip, REPLY_STATUSES, computeEnrichment,
  DIAGNOSIS_CONFIG, findDuplicateStakeholder, confirmDuplicateStakeholder,
  deriveStakeholderStatus, updateStakeholderStatus, STAKEHOLDER_STATUS_PRIORITY,
  STAKEHOLDER_STATUS_PROTECTED, activateAccountIfNeeded,
  formatCurrency, formatDate, strSimilarity, FileNotesRenderer,
} from '../utils.jsx';
import { confirmNoDuplicate } from '../teamClient.js';
import StakeholderHistoryModal from './StakeholderHistoryModal.jsx';
import AIMessageModal from './AIMessageModal.jsx';
import EmailComposeModal from './EmailComposeModal.jsx';
import { gmailSyncResult, rememberGmailCoverage } from '../gmailSync.js';
import { useGmailTextChoice } from './ai/GmailTextChoice.jsx';
import { quickFollowupPrompt, shortFollowupPrompt, replyTaskPrompt, contactActivities, lastSentOf } from '../commercialPrompts.js';
import { pendingReplies, focusItems, outreachByContact, DISMISS_FOLLOWUPS_KEY, DISMISS_REPLIES_KEY as DISMISS_REPLIES_STORAGE } from '../followupQueue.js';
import { countsAsInteraction } from '../outreachEvidence.js';


function FollowupCenter({ data, api, onLogActivity, onAddRecord, onUpdateRecord, goToAccount, embedded = false }) {
  // Email text goes to the AI only when Ale marks it before generating (src/outreachEvidence.js › aiTextOf)
  const gmailChoice = useGmailTextChoice();
  const { accounts, stakeholders, outreach, actionPlan = [] } = data;
  const [accountSearch, setAccountSearch] = useState('');
  const [selectedInfluence, setSelectedInfluence] = useState('');
  const [searchName, setSearchName] = useState('');
  const [selectedStakeholder, setSelectedStakeholder] = useState(null);
  const [responseModal, setResponseModal] = useState(null);
  const [responseText, setResponseText] = useState('');
  const [meetingModal, setMeetingModal] = useState(null);
  const [meetingNotes, setMeetingNotes] = useState('');
  const [meetingDate, setMeetingDate] = useState('');
  const [meetingTime, setMeetingTime] = useState('');
  const [historyStakeholder, setHistoryStakeholder] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [csvRows, setCsvRows] = useState([]);
  const [generatingFollowup, setGeneratingFollowup] = useState(null);
  const [csvStatus, setCsvStatus] = useState(null);
  const [importResults, setImportResults] = useState(null);
  const [expandedReplies, setExpandedReplies] = useState({});
  const [composeEmail, setComposeEmail] = useState(null);
  const [showDismissed, setShowDismissed] = useState(false);
  const [showDismissedReplies, setShowDismissedReplies] = useState(false);
  const [openCard, setOpenCard] = useState(null);
  const [generatedMsgs, setGeneratedMsgs] = useState({});

  const DISMISS_REPLIES_KEY = DISMISS_REPLIES_STORAGE;
  const [dismissedReplies, setDismissedReplies] = useState(() => {
    try {
      const raw = JSON.parse(localStorage.getItem(DISMISS_REPLIES_KEY) || '{}');
      const today = new Date().toDateString();
      const filtered = {};
      Object.entries(raw).forEach(([id, date]) => { if (date === today) filtered[id] = date; });
      return filtered;
    } catch { return {}; }
  });
  const dismissReply = (stakeholderId) => {
    const today = new Date().toDateString();
    const next = { ...dismissedReplies, [stakeholderId]: today };
    setDismissedReplies(next);
    try { localStorage.setItem(DISMISS_REPLIES_KEY, JSON.stringify(next)); } catch {}
  };
  const undismissReply = (stakeholderId) => {
    const next = { ...dismissedReplies };
    delete next[stakeholderId];
    setDismissedReplies(next);
    try { localStorage.setItem(DISMISS_REPLIES_KEY, JSON.stringify(next)); } catch {}
  };

  const DISMISS_KEY = DISMISS_FOLLOWUPS_KEY;
  const [dismissedFollowups, setDismissedFollowups] = useState(() => {
    try {
      const raw = JSON.parse(localStorage.getItem(DISMISS_KEY) || '{}');
      const today = new Date().toDateString();
      const filtered = {};
      Object.entries(raw).forEach(([id, date]) => { if (date === today) filtered[id] = date; });
      return filtered;
    } catch { return {}; }
  });

  const dismissFollowup = (stakeholderId) => {
    const today = new Date().toDateString();
    const next = { ...dismissedFollowups, [stakeholderId]: today };
    setDismissedFollowups(next);
    try { localStorage.setItem(DISMISS_KEY, JSON.stringify(next)); } catch {}
  };
  const undismissFollowup = (stakeholderId) => {
    const next = { ...dismissedFollowups };
    delete next[stakeholderId];
    setDismissedFollowups(next);
    try { localStorage.setItem(DISMISS_KEY, JSON.stringify(next)); } catch {}
  };
  const [showFuNewStk, setShowFuNewStk] = useState(false);
  const [fuNewName, setFuNewName] = useState('');
  const [fuNewLast, setFuNewLast] = useState('');
  const [fuNewRole, setFuNewRole] = useState('');
  const [fuNewEmail, setFuNewEmail] = useState('');
  const [fuNewPhone, setFuNewPhone] = useState('');
  const [fuNewLinkedin, setFuNewLinkedin] = useState('');
  const [fuNewInfluence, setFuNewInfluence] = useState('');
  const [fuNewAccountId, setFuNewAccountId] = useState('');
  const [fuCreating, setFuCreating] = useState(false);

  const [logSaving, setLogSaving] = useState(false);
  const [logError, setLogError] = useState('');
  const [gmailSyncing, setGmailSyncing] = useState(false);
  const [gmailSyncMsg, setGmailSyncMsg] = useState(null);
  const gmailConnectedFC = localStorage.getItem('oike_gmail_connected') === 'true';

  // `cursor`: «Continuar» a partial reading where it stopped (what was read and saved is kept)
  const handleGmailSyncFC = async (cursor = null) => {
    setGmailSyncing(true);
    setGmailSyncMsg('');
    let result;
    try {
      const res = await fetch('/api/gmail/sync', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ baseId: AIRTABLE_BASE_ID, stakeholdersTableId: TABLE_IDS.stakeholders, outreachTableId: TABLE_IDS.outreach, daysBack: 7, ...(cursor ? { cursor } : {}) }),
      });
      const d = await res.json().catch(() => null);
      result = gmailSyncResult(res.status, d);
      rememberGmailCoverage(d);
    } catch (e) {
      result = gmailSyncResult(0, null);
    }
    setGmailSyncMsg(result);
    if (result.reload && onLogActivity) onLogActivity();
    setGmailSyncing(false);
  };

  // Search, influence and name filters of this screen (the queue itself is src/followupQueue.js, shared with Mi espacio › Hoy)
  const keepFiltered = useCallback((s) => {
    if (accountSearch) {
      const term = accountSearch.toLowerCase();
      const accNames = resolveLinked(s, 'Account', accounts, 'Account Name');
      if (!accNames.some(n => n.toLowerCase().includes(term))) return false;
    }
    if (selectedInfluence && F(s, 'Level of Influence') !== selectedInfluence) return false;
    if (searchName && !(F(s, 'Name') || '').toLowerCase().includes(searchName.toLowerCase())) return false;
    return true;
  }, [accounts, accountSearch, selectedInfluence, searchName]);

  // Activity per contact, once. Records logged only because a channel was opened never count (src/outreachEvidence.js)
  const byContact = useMemo(() => outreachByContact(outreach), [outreach]);
  const repliedToYou = useMemo(() => pendingReplies({ stakeholders, byContact, keep: keepFiltered }), [stakeholders, byContact, keepFiltered]);

  const recentlyContacted = useMemo(() => {
    const cutoff = new Date(); cutoff.setDate(cutoff.getDate() - 3); cutoff.setHours(0, 0, 0, 0);
    const ids = new Set();
    outreach.filter(countsAsInteraction).forEach(o => {
      const d = o.fields?.['Date'] ? new Date(o.fields['Date']) : null;
      if (d && d >= cutoff) linkedIds(o, 'Stakeholder').forEach(id => ids.add(id));
    });
    return ids;
  }, [outreach]);

  const dailyFocusItems = useMemo(() => focusItems({ stakeholders, byContact, accounts, campaigns: data.campaigns || [], keep: keepFiltered }), [stakeholders, byContact, accounts, data.campaigns, keepFiltered]);

  const activeFocusItems    = dailyFocusItems.filter(item => !dismissedFollowups[item.s.id]);
  const dismissedFocusItems = dailyFocusItems.filter(item =>  dismissedFollowups[item.s.id]);
  const urgentItems   = activeFocusItems.filter(item => item.tag === 'urgent');
  const followupItems = activeFocusItems.filter(item => item.tag === 'followup');
  const reengageItems = activeFocusItems.filter(item => item.tag === 'reengage');

  const followupPending = dailyFocusItems;

  // ── Manual sequence steps due today (WhatsApp / LinkedIn — can't auto-send) ──
  const manualSeqStepsDueToday = useMemo(() => {
    const campaigns = data.campaigns || [];
    const now = new Date();
    const results = [];
    campaigns.forEach(campaign => {
      let steps, enrollments, seqCfg;
      try {
        steps = JSON.parse(F(campaign, 'Sequence Steps') || '[]');
        enrollments = JSON.parse(F(campaign, 'Sequence Enrollments') || '{}');
        seqCfg = JSON.parse(F(campaign, 'Sequence Config') || '{}');
      } catch { return; }
      if (!steps.length || seqCfg.active === false) return;
      Object.entries(enrollments).forEach(([stkId, en]) => {
        if (en.status !== 'active') return;
        const step = steps[en.step];
        if (!step) return;
        if (step.channel === 'Email') return; // auto-sent by runner
        const dt = en.nextDateTime ? new Date(en.nextDateTime) : (en.nextDate ? new Date(en.nextDate) : null);
        if (!dt || dt > now) return;
        const stk = stakeholders.find(s => s.id === stkId);
        if (!stk) return;
        const accId = (stk.fields?.['Account'] || [])[0];
        const acc = accId ? accounts.find(a => a.id === accId) : null;
        results.push({ stk, acc, campaign, step, en, stepIdx: en.step });
      });
    });
    return results.sort((a, b) => {
      const da = a.en.nextDateTime || a.en.nextDate || '';
      const db = b.en.nextDateTime || b.en.nextDate || '';
      return da.localeCompare(db);
    });
  }, [data.campaigns, stakeholders, accounts]);

  const [dismissedManualSteps, setDismissedManualSteps] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem('oike_dismissed_manual_steps') || '[]')); }
    catch { return new Set(); }
  });
  const dismissManualStep = (key) => {
    setDismissedManualSteps(prev => {
      const next = new Set(prev); next.add(key);
      try { localStorage.setItem('oike_dismissed_manual_steps', JSON.stringify([...next])); } catch {}
      return next;
    });
  };

  const DONE_STATUSES = new Set(['Completado', 'Cerrado', 'Done', 'Closed']);
  const urgentActions = useMemo(() => {
    const todayIso = new Date().toISOString().split('T')[0];
    return (actionPlan || [])
      .filter(a => {
        const source = F(a, 'Source') || '';
        if (!source.startsWith('AI')) return false;
        const status = F(a, 'Status') || '';
        if (DONE_STATUSES.has(status)) return false;
        const snoozed = F(a, 'Snoozed Until');
        if (snoozed && snoozed >= todayIso) return false;
        return true;
      })
      .sort((a, b) => {
        const urgWeight = { High: 3, Medium: 2, Low: 1 };
        const ua = urgWeight[F(a, 'Urgency')] || 0;
        const ub = urgWeight[F(b, 'Urgency')] || 0;
        if (ub !== ua) return ub - ua;
        return (a.createdTime || '').localeCompare(b.createdTime || '');
      });
  }, [actionPlan]);

  const [editingTaskId, setEditingTaskId] = useState(null);
  const [editingTaskText, setEditingTaskText] = useState('');
  const [taskBusyId, setTaskBusyId] = useState(null);

  const markTaskDone = async (task) => {
    setTaskBusyId(task.id);
    if (onUpdateRecord) onUpdateRecord('actionPlan', task.id, { 'Status': 'Completado' });
    try {
      const a = api || new AirtableAPI();
      if (!String(task.id).startsWith('tmp_')) {
        await a.updateRecord(TABLE_IDS.actionPlan, task.id, { 'Status': 'Completado' });
      }
    } catch (e) { console.error('Mark done failed:', e); }
    finally { setTaskBusyId(null); }
  };

  const snoozeTask = async (task, days = 3) => {
    setTaskBusyId(task.id);
    const until = new Date(); until.setDate(until.getDate() + days);
    const iso = until.toISOString().split('T')[0];
    if (onUpdateRecord) onUpdateRecord('actionPlan', task.id, { 'Snoozed Until': iso });
    try {
      const a = api || new AirtableAPI();
      if (!String(task.id).startsWith('tmp_')) {
        await a.updateRecord(TABLE_IDS.actionPlan, task.id, { 'Snoozed Until': iso });
      }
    } catch (e) { console.error('Snooze failed:', e); }
    finally { setTaskBusyId(null); }
  };

  const saveTaskEdit = async (task) => {
    const newText = editingTaskText.trim();
    if (!newText) { setEditingTaskId(null); return; }
    setTaskBusyId(task.id);
    if (onUpdateRecord) onUpdateRecord('actionPlan', task.id, { 'Nombre de la Acción': newText });
    try {
      const a = api || new AirtableAPI();
      if (!String(task.id).startsWith('tmp_')) {
        await a.updateRecord(TABLE_IDS.actionPlan, task.id, { 'Nombre de la Acción': newText });
      }
    } catch (e) { console.error('Edit task failed:', e); }
    finally { setTaskBusyId(null); setEditingTaskId(null); }
  };

  const INTENT_BADGE = {
    meeting_request:     { label: 'Pide una reunión', color: '#1e40af', bg: 'rgba(30,64,175,0.12)' },
    info_request:        { label: 'Pide información',    color: '#4338ca', bg: 'rgba(67,56,202,0.12)' },
    objection:           { label: 'Objeción',       color: '#b91c1c', bg: 'rgba(185,28,28,0.12)' },
    follow_up_needed:    { label: 'Seguimiento',       color: '#92400e', bg: 'rgba(146,64,14,0.12)' },
    engaged:             { label: 'Interesado',          color: '#065f46', bg: 'rgba(6,95,70,0.12)' },
    introduction_needed: { label: 'Pide una presentación',    color: '#6b21a8', bg: 'rgba(107,33,168,0.12)' },
    not_now:             { label: 'Ahora no',         color: '#374151', bg: 'rgba(55,65,81,0.12)' },
    ghosted:             { label: 'Sin respuesta',         color: '#6b7280', bg: 'rgba(107,114,128,0.12)' },
    other:               { label: 'Respuesta',           color: '#374151', bg: 'rgba(55,65,81,0.12)' },
  };

  const logResponse = async (stakeholder, responseContent) => {
    const name = F(stakeholder, 'Name') || '';
    const companyIds = linkedIds(stakeholder, 'Account');
    const a = api || new AirtableAPI();
    try {
      await a.createRecord(TABLE_IDS.outreach, {
        'Activity Name': `Reply from ${name} — ${new Date().toLocaleDateString('en-US')}`,
        'Account': companyIds,
        'Stakeholder': [stakeholder.id],
        'Channel': 'Email',
        'Date': new Date().toISOString(),
        'Status': 'Replied',
        'Message': responseContent,
        'Notes': 'Stakeholder responded — logged from Follow-up Center',
        'Logged By': CURRENT_USER?.name || '',
        ...(CURRENT_USER?.role === 'bdr' && CURRENT_USER?.name ? { 'BDR Owner': CURRENT_USER.name } : {}),
        ...(CURRENT_USER?.role === 'cp' && CURRENT_USER?.name ? { 'CP Assigned': CURRENT_USER.name } : {}),
      });
    } catch (e) {
      // Not saved: the modal stays open with the text to retry
      console.error('Log response failed:', e);
      throw new Error('No se pudo registrar la respuesta: ' + e.message);
    }
    try {
      await activateAccountIfNeeded(a, companyIds, data.accounts);
      await updateStakeholderStatus(a, stakeholder.id, 'Replied', data.stakeholders);
    } catch (e) { window.__oikeToast?.('La respuesta quedó registrada, pero no se pudo actualizar el estado del contacto.', 'warning'); }
    if (onLogActivity) onLogActivity();
  };

  const logMeeting = async (stakeholder, notes, date) => {
    const name = F(stakeholder, 'Name') || '';
    const companyIds = linkedIds(stakeholder, 'Account');
    const a = api || new AirtableAPI();
    try {
      await a.createRecord(TABLE_IDS.outreach, {
        'Activity Name': `Meeting Scheduled: ${name} — ${new Date().toLocaleDateString('en-US')}`,
        'Account': companyIds,
        'Stakeholder': [stakeholder.id],
        'Channel': 'Meeting',
        'Date': new Date().toISOString(),
        'Status': 'Meeting Scheduled',
        'Message': notes || '',
        'Notes': `Meeting ${date ? `on ${date}` : 'TBD'} — logged from Follow-up Center`,
        'Logged By': CURRENT_USER?.name || '',
        ...(CURRENT_USER?.role === 'bdr' && CURRENT_USER?.name ? { 'BDR Owner': CURRENT_USER.name } : {}),
        ...(CURRENT_USER?.role === 'cp' && CURRENT_USER?.name ? { 'CP Assigned': CURRENT_USER.name } : {}),
      });
    } catch (e) {
      console.error('Log meeting failed:', e);
      throw new Error('No se pudo registrar la reunión: ' + e.message);
    }
    try {
      await activateAccountIfNeeded(a, companyIds, data.accounts);
      await updateStakeholderStatus(a, stakeholder.id, 'Meeting Booked', data.stakeholders);
    } catch (e) { window.__oikeToast?.('La reunión quedó registrada, pero no se pudo actualizar el estado del contacto.', 'warning'); }
    if (onLogActivity) onLogActivity();
  };

  const filtered = useMemo(() => stakeholders.filter(s => {
    if (recentlyContacted.has(s.id)) return false;
    const hasOutreach = outreach.some(o => linkedIds(o, 'Stakeholder').includes(s.id));
    if (hasOutreach) return false;
    if (accountSearch) {
      const term = accountSearch.toLowerCase();
      const accNames = resolveLinked(s, 'Account', accounts, 'Account Name');
      if (!accNames.some(n => n.toLowerCase().includes(term))) return false;
    }
    if (selectedInfluence && F(s, 'Level of Influence') !== selectedInfluence) return false;
    if (searchName && !(F(s, 'Name') || '').toLowerCase().includes(searchName.toLowerCase())) return false;
    return true;
  }), [stakeholders, accountSearch, accounts, selectedInfluence, searchName, recentlyContacted]);

  const handleQuickFollowup = async (s, channel) => {
    setGeneratingFollowup(s.id);
    try {
      const sName = F(s, 'Name') || '';
      const role = F(s, 'Role') || '';
      const accIds = linkedIds(s, 'Account') || [];
      const accNames = accIds.map(id => { const a = accounts.find(x => x.id === id); return a ? F(a, 'Account Name') : ''; }).filter(Boolean);
      const allSolutions = data.solutions || [];
      const accRecord = accIds[0] ? accounts.find(a => a.id === accIds[0]) : null;
      const linkedSolIds = accRecord ? (linkedIds(accRecord, 'Solutions') || []) : [];
      const relevantSolutions = linkedSolIds.length > 0
        ? allSolutions.filter(sol => linkedSolIds.includes(sol.id))
        : allSolutions.slice(0, 6);
      const offeringsText = relevantSolutions.map(sol => {
        const name = F(sol, 'Name') || '';
        const detail = (F(sol, 'Service | Solution Detail') || '').slice(0, 200);
        return `• ${name}${detail ? `: ${detail}` : ''}`;
      }).join('\n');
      // Continues the thread: the text of the Gmail messages it would use goes only if Ale marks it now
      const sOutreach = contactActivities(outreach, s.id);
      const used = [lastSentOf(sOutreach), ...sOutreach.slice(0, 5)].filter(Boolean);
      const allow = await gmailChoice.ask(used, { purpose: 'Para continuar la conversación, la IA usa el último mensaje que enviaste y el historial.' });
      if (!allow) { setGeneratingFollowup(null); return; }
      const prompt = quickFollowupPrompt({ company: COMPANY_PROFILE, offeringsText, accountName: accNames[0] || '', name: sName, role, records: sOutreach, channel, allow });
      const generated = await callOpenAI({ prompt, temperature: 0.8, max_tokens: 300, method: 'messages', accountId: accIds[0] });
      useMessage(s, channel, generated.trim());
    } catch (e) {
      console.error('Quick followup generation failed:', e);
      useMessage(s, channel, '');
    }
    setGeneratingFollowup(null);
  };

  const influenceLevels = useMemo(() => {
    const levels = new Set();
    stakeholders.forEach(s => { const l = F(s, 'Level of Influence'); if (l) levels.add(l); });
    return [...levels].sort();
  }, [stakeholders]);

  const accountsByTemp = useMemo(() => {
    const now = Date.now();
    const active = accounts.filter(a => {
      const status = F(a, 'Inside Sales Status') || '';
      return status !== 'Inactive' && status !== 'Lost';
    });
    const result = { HOT: [], WARM: [], COLD: [] };
    active.forEach(account => {
      // Try radarData first
      let temp = null;
      let nextAction = null;
      const radarRaw = account.fields?.['Radar Data'];
      if (radarRaw) {
        try {
          const rd = typeof radarRaw === 'string' ? JSON.parse(radarRaw) : radarRaw;
          temp = rd?.recommended_actions?.[0]?.temperature || null;
          nextAction = rd?.recommended_actions?.[0]?.action || null;
        } catch {}
      }
      // Fallback: compute from outreach days
      const accOutreach = outreach.filter(o => linkedIds(o, 'Account').includes(account.id))
        .sort((a, b) => new Date(b.fields?.['Date'] || 0) - new Date(a.fields?.['Date'] || 0));
      const lastOut = accOutreach[0];
      const daysSince = lastOut ? Math.floor((now - new Date(lastOut.fields?.['Date'])) / (1000*60*60*24)) : null;
      if (!temp) {
        temp = daysSince === null ? 'COLD' : daysSince <= 7 ? 'HOT' : daysSince <= 21 ? 'WARM' : 'COLD';
      }
      result[temp]?.push({ account, nextAction, daysSince });
    });
    // Sort each bucket by daysSince desc (most stale first in COLD, most recent first in HOT)
    result.HOT.sort((a, b) => (a.daysSince ?? 999) - (b.daysSince ?? 999));
    result.WARM.sort((a, b) => (a.daysSince ?? 999) - (b.daysSince ?? 999));
    result.COLD.sort((a, b) => (b.daysSince ?? 0) - (a.daysSince ?? 0));
    return result;
  }, [accounts, outreach]);

  const useMessage = (stakeholder, channel, message, ccList = [], eventId = null) => {
    const name = F(stakeholder, 'Name') || '';
    const email = F(stakeholder, 'Email') || '';
    const phone = F(stakeholder, 'Phone number') || '';
    const linkedin = F(stakeholder, 'LinkedIn') || '';
    let subject = '';
    let body = message;
    if (channel === 'Email') {
      const lines = message.split('\n');
      const subjectIdx = lines.findIndex(l => /^subject:/i.test(l.trim()));
      if (subjectIdx !== -1) {
        subject = lines[subjectIdx].replace(/^subject:\s*/i, '').trim();
        body = lines.slice(subjectIdx + 1).join('\n').trim();
      }
      if (email) {
        setComposeEmail({ stakeholder, subject, body, replyContext: null });
        return;
      }
    }
    if (channel === 'WhatsApp' && phone) window.open(`https://wa.me/${String(phone).replace(/[^0-9+]/g, '')}?text=${encodeURIComponent(message)}`, '_blank');
    else if (channel === 'LinkedIn' && linkedin) { navigator.clipboard.writeText(message).catch(() => {}); window.open(linkedin, '_blank'); }
    else if (channel === 'Call' && phone) window.open(`tel:${phone}`, '_self');
    const companyIds = linkedIds(stakeholder, 'Account');
    const outreachFields = {
      'Activity Name': `${channel} to ${name} — ${new Date().toLocaleDateString('en-US')}`,
      'Account': companyIds, 'Stakeholder': [stakeholder.id],
      'Channel': channel, 'Date': new Date().toISOString(),
      'Status': 'Sent', 'Message': message || '',
      'Notes': 'Auto-logged from Follow-up Center',
      'Logged By': CURRENT_USER?.name || '',
      ...(CURRENT_USER?.role === 'bdr' && CURRENT_USER?.name ? { 'BDR Owner': CURRENT_USER.name } : {}),
      ...(CURRENT_USER?.role === 'cp' && CURRENT_USER?.name ? { 'CP Assigned': CURRENT_USER.name } : {}),
    };
    if (onAddRecord) onAddRecord('outreach', outreachFields);
    const a = api || new AirtableAPI();
    a.createRecord(TABLE_IDS.outreach, outreachFields)
      .then(() => activateAccountIfNeeded(a, companyIds, data.accounts))
      .then(() => updateStakeholderStatus(a, stakeholder.id, 'Contacted', data.stakeholders))
      .then(async () => {
        if (eventId) {
          try {
            const evCached = (data.events || []).find(e => e.id === eventId);
            const currentInvited = evCached ? linkedIds(evCached, 'Stakeholders invited') : [];
            await a.updateRecord(TABLE_IDS.events, eventId, {
              'Stakeholders invited': [...new Set([...currentInvited, stakeholder.id])],
            });
          } catch(evErr) { console.error('[useMessage] event invite update failed:', evErr); }
        }
      })
      .then(() => { if (onLogActivity) onLogActivity(); })
      .catch(e => console.error('Auto-log failed:', e));
  };

  const suggestChannel = (s, lastChannel) => {
    const phone = F(s, 'Phone number');
    const linkedin = F(s, 'LinkedIn');
    const email = F(s, 'Email');
    if (lastChannel === 'Email' && phone) return 'WhatsApp';
    if (lastChannel === 'Email' && linkedin) return 'LinkedIn';
    if (lastChannel === 'WhatsApp' && linkedin) return 'LinkedIn';
    if (lastChannel === 'LinkedIn' && email) return 'Email';
    if (phone) return 'WhatsApp';
    if (linkedin) return 'LinkedIn';
    return 'Email';
  };

  const generateFollowupText = async (s, channel, allow = new Set()) => {
    const sName = F(s, 'Name') || '';
    const role = F(s, 'Role') || '';
    const accIds = linkedIds(s, 'Account') || [];
    const accNames = accIds.map(id => { const a = accounts.find(x => x.id === id); return a ? F(a, 'Account Name') : ''; }).filter(Boolean);
    const allSolutions = data.solutions || [];
    const accRecord = accIds[0] ? accounts.find(a => a.id === accIds[0]) : null;
    const linkedSolIds = accRecord ? (linkedIds(accRecord, 'Solutions') || []) : [];
    const relevantSolutions = linkedSolIds.length > 0 ? allSolutions.filter(sol => linkedSolIds.includes(sol.id)) : allSolutions.slice(0, 4);
    const offeringsText = relevantSolutions.map(sol => `• ${F(sol, 'Name') || ''}${(F(sol, 'Service | Solution Detail') || '').slice(0, 150) ? ': ' + (F(sol, 'Service | Solution Detail') || '').slice(0, 150) : ''}`).join('\n');
    const prompt = shortFollowupPrompt({ company: COMPANY_PROFILE, offeringsText, accountName: accNames[0] || '', name: sName, role, records: contactActivities(outreach, s.id), channel, allow });
    const generated = await callOpenAI({ prompt, temperature: 0.8, max_tokens: 300, method: 'messages', accountId: accIds[0] });
    return generated.trim();
  };

  // The Gmail messages a follow-up of these contacts would use (the last one sent and the recent history)
  const usedFor = (s) => { const list = contactActivities(outreach, s.id); return [lastSentOf(list), ...list.slice(0, 4)].filter(Boolean); };
  const FOLLOWUP_PURPOSE = 'Para continuar cada conversación, la IA usa el último mensaje que enviaste y el historial.';
  const generateForContact = async (s, given = null) => {
    const allow = given || await gmailChoice.ask(usedFor(s), { purpose: FOLLOWUP_PURPOSE });
    if (!allow) return;
    const lastChannel = F(s._enriched?.lastOutreach, 'Channel');
    const channel = suggestChannel(s, lastChannel);
    setGeneratedMsgs(prev => ({ ...prev, [s.id]: { text: '', channel, loading: true } }));
    try {
      const text = await generateFollowupText(s, channel, allow);
      setGeneratedMsgs(prev => ({ ...prev, [s.id]: { text, channel, loading: false } }));
    } catch {
      setGeneratedMsgs(prev => ({ ...prev, [s.id]: { text: '', channel, loading: false } }));
    }
  };

  const generateBatch = async (items) => {
    const first5 = items.slice(0, 5);
    // One question for the whole batch, before generating
    const names = new Map(first5.map(({ s }) => [s.id, `${F(s, 'Name') || ''} ${F(s, 'Last name') || ''}`.trim()]));
    const allow = await gmailChoice.ask(first5.flatMap(({ s }) => usedFor(s)), { purpose: FOLLOWUP_PURPOSE, nameOf: (o) => names.get(linkedIds(o, 'Stakeholder')[0]) || '' });
    if (!allow) return;
    await Promise.all(first5.map(({ s }) => generateForContact(s, allow)));
  };

  const executeUrgentTask = async (task, channel) => {
    const stkName = F(task, 'Stakeholder') || '';
    const stkRec = stakeholders.find(s => {
      const full = `${F(s,'Name')||''} ${F(s,'Last name')||''}`.trim();
      return full && stkName && full.toLowerCase() === stkName.toLowerCase();
    });
    if (!stkRec) {
      window.__oikeToast(`No se encontró el contacto "${stkName}".`, 'warning');
      return;
    }
    const phone = F(stkRec, 'Phone number');
    const email = F(stkRec, 'Email');
    const linkedin = F(stkRec, 'LinkedIn');
    if (channel === 'Email' && !email) { window.__oikeToast('Este contacto no tiene email.', 'warning'); return; }
    if (channel === 'WhatsApp' && !phone) { window.__oikeToast('Este contacto no tiene teléfono.', 'warning'); return; }
    if (channel === 'LinkedIn' && !linkedin) { window.__oikeToast('Este contacto no tiene LinkedIn.', 'warning'); return; }
    setTaskBusyId(task.id);
    try {
      const intent = F(task, 'Intent') || 'other';
      const nextStep = F(task, 'Nombre de la Acción') || '';
      const accIds = linkedIds(task, 'Cuenta');
      const acc = accIds[0] ? accounts.find(a => a.id === accIds[0]) : null;
      const accName = acc ? F(acc, 'Account Name') : '';
      const role = F(stkRec, 'Role') || '';
      // Answers their reply: the text of the Gmail messages goes only if Ale marks it now
      const records = contactActivities(outreach, stkRec.id);
      const allow = await gmailChoice.ask(records.slice(0, 3), { purpose: 'Para responder, la IA usa la respuesta del contacto y los mensajes anteriores.' });
      if (!allow) return;
      const senderName = (typeof COMPANY_PROFILE !== 'undefined' && COMPANY_PROFILE.senderName) ? COMPANY_PROFILE.senderName : (CURRENT_USER?.name || '');
      const prompt = replyTaskPrompt({ senderName, name: stkName, role, accountName: accName, nextStep, intent, records, channel, allow });
      let message = '';
      try {
        const raw = await callOpenAI({ prompt, temperature: 0.7, max_tokens: 400, method: 'messages' });
        message = (raw || '').trim();
      } catch (e) {
        console.error('AI message gen failed:', e);
        message = nextStep;
      }
      useMessage(stkRec, channel, message);
      if (onUpdateRecord) onUpdateRecord('actionPlan', task.id, { 'Status': 'Completado' });
      if (!String(task.id).startsWith('tmp_')) {
        const a = api || new AirtableAPI();
        a.updateRecord(TABLE_IDS.actionPlan, task.id, { 'Status': 'Completado' })
          .catch(e => console.error('Mark task done (after execute) failed:', e));
      }
    } finally {
      setTaskBusyId(null);
    }
  };

  const fuCreateStakeholder = async () => {
    if (!fuNewName.trim() || !fuNewAccountId) return;
    const fields = { 'Name': fuNewName.trim(), 'Account': [fuNewAccountId] };
    if (fuNewLast.trim()) fields['Last name'] = fuNewLast.trim();
    if (fuNewRole.trim()) fields['Role'] = fuNewRole.trim();
    if (fuNewEmail.trim()) fields['Email'] = fuNewEmail.trim();
    if (fuNewPhone.trim()) fields['Phone number'] = fuNewPhone.trim();
    if (fuNewLinkedin.trim()) fields['LinkedIn'] = fuNewLinkedin.trim();
    if (fuNewInfluence) fields['Level of Influence'] = fuNewInfluence;
    if (CURRENT_USER?.role === 'bdr' && CURRENT_USER?.name) fields['BDR Owner'] = CURRENT_USER.name;
    if (CURRENT_USER?.role === 'cp' && CURRENT_USER?.name) fields['CP Assigned'] = CURRENT_USER.name;
    if (!(await confirmNoDuplicate('contact', fields))) return;
    const dup = findDuplicateStakeholder(fields, stakeholders);
    if (dup && !confirmDuplicateStakeholder(dup)) return;
    if (onAddRecord) onAddRecord('stakeholders', fields);
    setFuNewName(''); setFuNewLast(''); setFuNewRole(''); setFuNewEmail('');
    setFuNewPhone(''); setFuNewLinkedin(''); setFuNewInfluence(''); setFuNewAccountId('');
    setShowFuNewStk(false);
    const a = api || new AirtableAPI();
    a.createRecord(TABLE_IDS.stakeholders, fields)
      .then(() => { if (onLogActivity) onLogActivity(); })
      .catch(e => { console.error(e); window.__oikeToast('No se pudo crear el contacto', 'error'); if (onLogActivity) onLogActivity(); });
  };

  const StakeholderRow = ({ s }) => {
    const accountNames = resolveLinked(s, 'Account', accounts, 'Account Name');
    const isDone = s.fields?.['Hecho'] === true;
    const phone = F(s, 'Phone number');
    const email = F(s, 'Email');
    const linkedin = F(s, 'LinkedIn');
    return (
      <tr key={s.id}>
        <td>
          <div style={{ fontWeight: 600, cursor: 'pointer', color: 'var(--globant-green)' }} onClick={() => setHistoryStakeholder(s)}>{F(s, 'Name')}{F(s, 'Last name') ? ` ${F(s, 'Last name')}` : ''}</div>
          <div style={{ fontSize: 11, color: 'var(--globant-muted)' }}>{F(s, 'Role')}</div>
        </td>
        <td style={{ fontSize: 12 }}>{accountNames.join(', ')}</td>
        <td><span className="badge badge-accent">{F(s, 'Level of Influence')}</span></td>
        <td style={{ textAlign: 'center' }}>{isDone ? <span style={{ color: 'var(--globant-success)' }}>✅</span> : <span style={{ color: 'var(--globant-muted)' }}>—</span>}</td>
        <td>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            <button className="action-btn btn-primary" style={{ fontSize: 11 }} onClick={() => setSelectedStakeholder(s)}>Mensaje</button>
            {phone && <button className="action-btn btn-whatsapp" style={{ fontSize: 11, padding: '4px 8px' }} onClick={() => setSelectedStakeholder(s)}>💬</button>}
            {email && <button className="action-btn btn-email" style={{ fontSize: 11, padding: '4px 8px' }} onClick={() => setSelectedStakeholder(s)}>✉️</button>}
            {linkedin && <button className="action-btn btn-linkedin" style={{ fontSize: 11, padding: '4px 8px' }} onClick={() => setSelectedStakeholder(s)}>🔗</button>}
            {phone && <button className="action-btn btn-call" style={{ fontSize: 11, padding: '4px 8px' }} onClick={() => useMessage(s, 'Call', '')}>📞</button>}
            <button className="action-btn" style={{ fontSize: 11, padding: '4px 8px', background: 'rgba(96,165,250,0.15)', color: '#2D5F99', border: '1px solid rgba(96,165,250,0.3)' }}
              onClick={() => { setMeetingModal({ stakeholder: s }); setLogError(''); setMeetingNotes(''); setMeetingDate(''); setMeetingTime(''); }}>📅</button>
          </div>
        </td>
      </tr>
    );
  };

  return (
    <div>
      {gmailChoice.element}
      {!embedded && (
        <div className="page-header">
          <h1>Seguimiento de hoy</h1>
          <p>{new Date().toLocaleDateString('es', { weekday: 'long', month: 'long', day: 'numeric' })}</p>
        </div>
      )}

      {gmailConnectedFC && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, padding: '8px 14px', background: 'rgba(91,191,181,0.06)', border: '1px solid rgba(91,191,181,0.15)', borderRadius: 8 }}>
          <span role={gmailSyncMsg?.tone === 'error' ? 'alert' : 'status'} style={{ fontSize: 12, color: gmailSyncMsg?.tone === 'error' ? 'var(--globant-danger)' : gmailSyncMsg?.tone === 'ok' ? 'var(--globant-success)' : 'var(--globant-muted)', flex: 1 }}>{gmailSyncMsg?.text || 'Sincroniza Gmail para ver las respuestas nuevas de tus contactos.'}</span>
          <button className="action-btn" style={{ background: 'rgba(91,191,181,0.15)', color: 'var(--globant-green)', border: '1px solid rgba(91,191,181,0.3)', fontSize: 11, padding: '5px 12px', flexShrink: 0 }} onClick={() => handleGmailSyncFC()} disabled={gmailSyncing}>{gmailSyncing ? 'Sincronizando…' : 'Sincronizar bandeja'}</button>
          {gmailSyncMsg?.cursor && !gmailSyncing && <button className="action-btn btn-primary" style={{ fontSize: 11, padding: '5px 12px', flexShrink: 0 }} onClick={() => handleGmailSyncFC(gmailSyncMsg.cursor)}>Continuar</button>}
        </div>
      )}

      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>Empresas activas</h2>
          <span style={{ fontSize: 11, color: 'var(--globant-muted)' }}>{(accountsByTemp.HOT.length + accountsByTemp.WARM.length + accountsByTemp.COLD.length)} empresas activas</span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
          {[
            { key: 'HOT', label: 'Calientes', color: '#B4362C', bg: 'rgba(239,68,68,0.06)', border: 'rgba(239,68,68,0.2)' },
            { key: 'WARM', label: 'Tibias', color: '#A0521A', bg: 'rgba(251,146,60,0.07)', border: 'rgba(251,146,60,0.22)' },
            { key: 'COLD', label: 'Frías', color: '#2D5F99', bg: 'rgba(96,165,250,0.06)', border: 'rgba(96,165,250,0.2)' },
          ].map(({ key, label, color, bg, border }) => {
            const col = accountsByTemp[key] || [];
            return (
              <div key={key} style={{ background: bg, border: `1px solid ${border}`, borderRadius: 12, padding: '14px 16px' }}>
                <div style={{ fontSize: 13, fontWeight: 700, color, marginBottom: 10 }}>{label} · {col.length}</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {col.slice(0, 5).map(({ account, nextAction, daysSince }) => {
                    const accName = F(account, 'Account Name') || account.id;
                    return (
                      <button type="button" key={account.id} style={{ display: 'block', width: '100%', textAlign: 'left', font: 'inherit', background: 'var(--globant-card)', border: '1px solid var(--globant-border)', borderRadius: 10, padding: '10px 12px', cursor: 'pointer' }}
                        onClick={() => goToAccount && goToAccount(account.id)}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--globant-text)', marginBottom: 3 }}>{accName}</div>
                        {nextAction && <div style={{ fontSize: 11, color: 'var(--globant-muted)', lineHeight: 1.4 }}>{nextAction}</div>}
                        {daysSince !== null && <div style={{ fontSize: 10, color: daysSince > 21 ? '#B4362C' : daysSince > 7 ? '#9A6212' : '#2D5F99', marginTop: 4 }}>hace {daysSince} días sin contacto</div>}
                      </button>
                    );
                  })}
                  {col.length > 5 && <div style={{ fontSize: 11, color: 'var(--globant-muted)', textAlign: 'center' }}>+{col.length - 5} más</div>}
                  {col.length === 0 && <div style={{ fontSize: 11, color: 'var(--globant-muted)', fontStyle: 'italic' }}>Sin empresas en este estado</div>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginBottom: 20 }}>
        <div onClick={() => setOpenCard(openCard === 'replied' ? null : 'replied')} style={{ cursor: 'pointer', padding: '20px 16px', borderRadius: 12, background: openCard === 'replied' ? 'rgba(74,222,128,0.12)' : 'var(--globant-card)', border: `2px solid ${openCard === 'replied' ? '#1E7B4F' : repliedToYou.length > 0 ? 'rgba(74,222,128,0.4)' : 'var(--globant-border)'}`, transition: 'all 0.2s' }}>
          <div style={{ fontSize: 28, fontWeight: 800, color: '#1E7B4F', marginBottom: 4 }}>{repliedToYou.length}</div>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--globant-text)', marginBottom: 2 }}>Te respondieron</div>
          <div style={{ fontSize: 11, color: 'var(--globant-muted)' }}>Te toca responder</div>
        </div>
        <div onClick={() => { setOpenCard(openCard === 'urgent' ? null : 'urgent'); if (openCard !== 'urgent' && urgentItems.length > 0) generateBatch(urgentItems); }} style={{ cursor: 'pointer', padding: '20px 16px', borderRadius: 12, background: openCard === 'urgent' ? 'rgba(239,68,68,0.1)' : 'var(--globant-card)', border: `2px solid ${openCard === 'urgent' ? '#B4362C' : urgentItems.length > 0 ? 'rgba(239,68,68,0.4)' : 'var(--globant-border)'}`, transition: 'all 0.2s' }}>
          <div style={{ fontSize: 28, fontWeight: 800, color: '#B4362C', marginBottom: 4 }}>{urgentItems.length}</div>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--globant-text)', marginBottom: 2 }}>Hacer seguimiento</div>
          <div style={{ fontSize: 11, color: 'var(--globant-muted)' }}>3 a 7 días: el mejor momento</div>
        </div>
        <div onClick={() => { setOpenCard(openCard === 'followup' ? null : 'followup'); if (openCard !== 'followup' && followupItems.length > 0) generateBatch(followupItems); }} style={{ cursor: 'pointer', padding: '20px 16px', borderRadius: 12, background: openCard === 'followup' ? 'rgba(251,191,36,0.1)' : 'var(--globant-card)', border: `2px solid ${openCard === 'followup' ? '#9A6212' : followupItems.length > 0 ? 'rgba(251,191,36,0.4)' : 'var(--globant-border)'}`, transition: 'all 0.2s' }}>
          <div style={{ fontSize: 28, fontWeight: 800, color: '#9A6212', marginBottom: 4 }}>{followupItems.length}</div>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--globant-text)', marginBottom: 2 }}>Atrasados</div>
          <div style={{ fontSize: 11, color: 'var(--globant-muted)' }}>8 a 21 días: que no se enfríen</div>
        </div>
        <div onClick={() => { setOpenCard(openCard === 'reengage' ? null : 'reengage'); if (openCard !== 'reengage' && reengageItems.length > 0) generateBatch(reengageItems); }} style={{ cursor: 'pointer', padding: '20px 16px', borderRadius: 12, background: openCard === 'reengage' ? 'rgba(167,139,250,0.1)' : 'var(--globant-card)', border: `2px solid ${openCard === 'reengage' ? '#6B5CA5' : reengageItems.length > 0 ? 'rgba(167,139,250,0.4)' : 'var(--globant-border)'}`, transition: 'all 0.2s' }}>
          <div style={{ fontSize: 28, fontWeight: 800, color: '#6B5CA5', marginBottom: 4 }}>{reengageItems.length}</div>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--globant-text)', marginBottom: 2 }}>Retomar</div>
          <div style={{ fontSize: 11, color: 'var(--globant-muted)' }}>Más de 22 días: con otro enfoque</div>
        </div>
      </div>

      {openCard === 'replied' && (
        <div className="card" style={{ borderLeft: '3px solid #1E7B4F', marginBottom: 16 }}>
          <div className="card-header">
            <h3 style={{ color: '#1E7B4F' }}>Te respondieron ({repliedToYou.length})</h3>
            <span style={{ fontSize: 11, color: 'var(--globant-muted)' }}>Te escribieron: respóndeles</span>
          </div>
          {repliedToYou.length === 0 ? (
            <div style={{ padding: '24px', textAlign: 'center', color: 'var(--globant-muted)' }}>Todo al día</div>
          ) : repliedToYou.map(({ s, lastOutreach: lo, daysSince, openedAfter }) => {
            const accNames = resolveLinked(s, 'Account', accounts, 'Account Name');
            const email = F(s, 'Email') || '';
            const phone = F(s, 'Phone number') || '';
            const linkedin = F(s, 'LinkedIn') || '';
            const notes = F(lo, 'Notes') || '';
            const msgField = F(lo, 'Message') || '';
            const gmsgMatch = notes.match(/\[gmsg:([^\]]+)\]/);
            const gmsgId = gmsgMatch ? gmsgMatch[1] : null;
            const gmailLink = gmsgId ? `https://mail.google.com/mail/#inbox/${gmsgId}` : null;
            const strippedNotes = notes.replace(/^(\[g[^\]]+\])+\s*/,'').trim();
            const rawContent = msgField.trim() || strippedNotes;
            const contentLines = rawContent.split('\n');
            const hasGmsg = !!gmsgMatch && !msgField.trim();
            const subject = hasGmsg ? (contentLines[0] || '') : '';
            const bodyText = (hasGmsg ? contentLines.slice(1).join('\n') : rawContent).trim();
            return (
              <div key={s.id} style={{ padding: '14px 0', borderBottom: '1px solid var(--globant-border)', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 700, fontSize: 14, cursor: 'pointer', color: 'var(--globant-green)' }} onClick={() => setHistoryStakeholder(s)}>{F(s,'Name')} {F(s,'Last name')||''}</span>
                    <span style={{ fontSize: 11, color: 'var(--globant-muted)' }}>{F(s,'Role')||''}{accNames.length ? ` · ${accNames[0]}` : ''}</span>
                    <span style={{ fontSize: 11, color: '#1E7B4F', background: 'rgba(74,222,128,0.1)', padding: '2px 8px', borderRadius: 20 }}>{daysSince === 0 ? 'hoy' : `hace ${daysSince} d`}</span>
                    {openedAfter > 0 && <span style={{ fontSize: 11, color: '#9A6212' }} title="Abrir un canal no confirma que le respondiste">se abrió un canal, sin respuesta confirmada</span>}
                  </div>
                  {bodyText && <div style={{ fontSize: 12, color: 'var(--globant-muted)', background: 'rgba(74,222,128,0.06)', border: '1px solid rgba(74,222,128,0.18)', borderRadius: 8, padding: '8px 12px', marginBottom: 8 }}>{subject && <div style={{ fontWeight: 700, marginBottom: 4 }}>📧 {subject}</div>}{bodyText.slice(0, 250)}{bodyText.length > 250 ? '…' : ''}</div>}
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {email && <button className="action-btn btn-email" style={{ fontSize: 11, padding: '5px 12px' }} onClick={() => { const replyContext = { threadId: (notes.match(/\[gthread:([^\]]+)\]/) || [])[1] || null, inReplyTo: (notes.match(/\[gmsgid:([^\]]+)\]/) || [])[1] || null, readMsgId: gmsgId }; setComposeEmail({ stakeholder: s, subject: subject ? `Re: ${subject}` : '', body: '', replyContext }); }}>Responder</button>}
                    {phone && <button className="action-btn btn-whatsapp" style={{ fontSize: 11, padding: '5px 12px' }} onClick={() => window.open(`https://wa.me/${phone.replace(/\D/g,'')}`, '_blank')}>WhatsApp</button>}
                    {linkedin && <button className="action-btn btn-linkedin" style={{ fontSize: 11, padding: '5px 12px' }} onClick={() => window.open(linkedin, '_blank')}>LinkedIn</button>}
                    {gmailLink && <a href={gmailLink} target="_blank" rel="noreferrer" style={{ fontSize: 11, padding: '5px 12px', borderRadius: 6, background: 'rgba(96,165,250,0.12)', color: '#2D5F99', border: '1px solid rgba(96,165,250,0.25)', textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}>Gmail</a>}
                    <button className="action-btn btn-ghost" style={{ fontSize: 11, padding: '5px 12px' }} onClick={() => setHistoryStakeholder(s)}>Ver historial</button>
                    <button onClick={() => dismissReply(s.id)} style={{ fontSize: 11, padding: '5px 8px', borderRadius: 6, background: 'rgba(30,42,44,0.05)', border: '1px solid var(--globant-border)', color: 'var(--globant-muted)', cursor: 'pointer' }}>✕</button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {(['urgent', 'followup', 'reengage']).map(cardType => {
        const items = cardType === 'urgent' ? urgentItems : cardType === 'followup' ? followupItems : reengageItems;
        const color = cardType === 'urgent' ? '#B4362C' : cardType === 'followup' ? '#9A6212' : '#6B5CA5';
        const label = cardType === 'urgent' ? 'Hacer seguimiento' : cardType === 'followup' ? 'Seguimiento atrasado' : 'Retomar';
        if (openCard !== cardType) return null;
        return (
          <div key={cardType} className="card" style={{ borderLeft: `3px solid ${color}`, marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div>
                <h3 style={{ margin: 0, color, fontSize: 14 }}>{label} ({items.length})</h3>
                <span style={{ fontSize: 11, color: 'var(--globant-muted)' }}>
                  {cardType === 'urgent' ? 'El mejor momento para escribir' : cardType === 'followup' ? 'Atrasado: que no se enfríe' : 'Se está enfriando: prueba otro enfoque'}
                </span>
              </div>
              <button className="action-btn btn-primary" style={{ fontSize: 11 }} onClick={() => generateBatch(items)}>Generar todos</button>
            </div>
            {items.length === 0 ? (
              <div style={{ padding: '24px', textAlign: 'center', color: 'var(--globant-muted)', fontSize: 13 }}>Todo al día</div>
            ) : items.map(({ s, e }) => {
              const accNames = resolveLinked(s, 'Account', accounts, 'Account Name');
              const msgState = generatedMsgs[s.id];
              const phone = F(s, 'Phone number');
              const email = F(s, 'Email');
              const linkedin = F(s, 'LinkedIn');
              const lastChannel = F(e?.lastOutreach, 'Channel');
              const sugCh = msgState?.channel || suggestChannel(s, lastChannel);
              const chIcon = { Email: '✉️', WhatsApp: '💬', LinkedIn: '🔗', Call: '📞' };
              return (
                <div key={s.id} style={{ padding: '14px', marginBottom: 8, borderRadius: 8, background: 'rgba(30,42,44,0.02)', border: '1px solid var(--globant-border)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 700, fontSize: 14, cursor: 'pointer', color: 'var(--globant-green)' }} onClick={() => setHistoryStakeholder(s)}>{F(s,'Name')} {F(s,'Last name')||''}</span>
                      <span style={{ fontSize: 11, color: 'var(--globant-muted)' }}>{F(s,'Role')||''}{accNames.length ? ` · ${accNames[0]}` : ''}</span>
                      <span style={{ fontSize: 11, fontWeight: 700, color }}>{e.daysSince}d ago</span>
                      {sugCh && <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 10, background: `${color}22`, color, border: `1px solid ${color}44`, fontWeight: 600 }}>{chIcon[sugCh]} {sugCh} suggested</span>}
                    </div>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button className="action-btn btn-ghost" style={{ fontSize: 10, padding: '3px 8px' }} onClick={() => setHistoryStakeholder(s)}>Historial</button>
                      <button className="action-btn btn-ghost" style={{ fontSize: 10, padding: '3px 8px' }} onClick={() => dismissFollowup(s.id)}>✕</button>
                    </div>
                  </div>
                  <div style={{ position: 'relative', marginBottom: 10 }}>
                    {msgState?.loading ? (
                      <div style={{ padding: '12px', background: 'rgba(30,42,44,0.03)', borderRadius: 8, border: '1px solid var(--globant-border)', fontSize: 12, color: 'var(--globant-muted)', textAlign: 'center' }}>Generando…</div>
                    ) : (
                      <textarea
                        className="input-field"
                        style={{ width: '100%', minHeight: msgState?.text ? 80 : 44, resize: 'vertical', fontSize: 12, fontFamily: 'inherit', background: msgState?.text ? 'rgba(30,42,44,0.04)' : 'rgba(30,42,44,0.02)', color: msgState?.text ? 'var(--globant-text)' : 'var(--globant-muted)' }}
                        placeholder={msgState === undefined ? 'Toca Generar para crear un mensaje…' : 'No se pudo generar: escríbelo o vuelve a intentarlo'}
                        value={msgState?.text || ''}
                        onChange={e2 => setGeneratedMsgs(prev => ({ ...prev, [s.id]: { ...(prev[s.id] || {}), text: e2.target.value } }))}
                      />
                    )}
                    {!msgState?.loading && <button className="action-btn btn-primary" style={{ position: 'absolute', right: 6, bottom: 6, fontSize: 10, padding: '3px 8px' }} onClick={() => generateForContact(s)}>{msgState?.text ? 'Volver a generar' : 'Generar'}</button>}
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {email && <button className={`action-btn ${sugCh === 'Email' ? 'btn-email' : 'btn-ghost'}`} style={{ fontSize: 11, padding: '5px 12px' }} onClick={() => { useMessage(s, 'Email', msgState?.text || ''); setGeneratedMsgs(prev => { const n = {...prev}; delete n[s.id]; return n; }); dismissFollowup(s.id); }}>✉️ Email{sugCh === 'Email' ? ' ✓' : ''}</button>}
                    {phone && <button className={`action-btn ${sugCh === 'WhatsApp' ? 'btn-whatsapp' : 'btn-ghost'}`} style={{ fontSize: 11, padding: '5px 12px' }} onClick={() => { useMessage(s, 'WhatsApp', msgState?.text || ''); dismissFollowup(s.id); }}>WhatsApp{sugCh === 'WhatsApp' ? ' ✓' : ''}</button>}
                    {linkedin && <button className={`action-btn ${sugCh === 'LinkedIn' ? 'btn-linkedin' : 'btn-ghost'}`} style={{ fontSize: 11, padding: '5px 12px' }} onClick={() => { useMessage(s, 'LinkedIn', msgState?.text || ''); dismissFollowup(s.id); }}>LinkedIn{sugCh === 'LinkedIn' ? ' ✓' : ''}</button>}
                    {phone && <button className="action-btn btn-ghost" style={{ fontSize: 11, padding: '5px 12px' }} onClick={() => useMessage(s, 'Call', '')}>Llamar</button>}
                    <button className="action-btn" style={{ fontSize: 11, padding: '5px 12px', background: 'rgba(96,165,250,0.15)', color: '#2D5F99', border: '1px solid rgba(96,165,250,0.3)' }} onClick={() => { setResponseModal({ stakeholder: s, lastOutreach: e.lastOutreach }); setResponseText(''); setLogError(''); }}>Respondió</button>
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}

      {(() => {
        const visible = manualSeqStepsDueToday.filter(({ stk, campaign, stepIdx }) =>
          !dismissedManualSteps.has(`${stk.id}:${campaign.id}:${stepIdx}`)
        );
        if (!visible.length) return null;
        return (
          <div className="card" style={{ borderLeft: '3px solid #6B5CA5', background: 'rgba(167,139,250,0.03)', marginBottom: 16 }}>
            <div className="card-header" style={{ marginBottom: 12 }}>
              <h3 style={{ color: '#6B5CA5', margin: 0 }}>📬 Manual sequence steps — due today ({visible.length})</h3>
              <span style={{ fontSize: 11, color: 'var(--globant-muted)' }}>Pasos de WhatsApp y LinkedIn de las secuencias activas: se envían a mano</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {visible.map(({ stk, acc, campaign, step, stepIdx }) => {
                const key = `${stk.id}:${campaign.id}:${stepIdx}`;
                const name = `${F(stk,'Name')}${F(stk,'Last name') ? ` ${F(stk,'Last name')}` : ''}`;
                const role = F(stk,'Role');
                const accName = acc ? F(acc,'Account Name') : '';
                const phone = F(stk,'Phone number');
                const linkedin = F(stk,'LinkedIn');
                const chColor = step.channel === 'LinkedIn' ? '#6366f1' : '#1E7B4F';
                const chBg = step.channel === 'LinkedIn' ? 'rgba(99,102,241,0.15)' : 'rgba(74,222,128,0.12)';
                const chIcon = step.channel === 'LinkedIn' ? '🔗' : '💬';
                const campName = F(campaign,'Name');
                const totalSteps = (() => { try { return JSON.parse(F(campaign,'Sequence Steps')||'[]').length; } catch { return '?'; } })();
                return (
                  <div key={key} style={{ display: 'flex', gap: 12, padding: '12px 14px', borderRadius: 8, background: 'rgba(167,139,250,0.05)', border: '1px solid rgba(167,139,250,0.2)', alignItems: 'flex-start' }}>
                    <div style={{ width: 32, height: 32, borderRadius: '50%', background: chBg, border: `2px solid ${chColor}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, flexShrink: 0, marginTop: 2 }}>{chIcon}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                        <span style={{ fontWeight: 700, fontSize: 13, color: 'var(--globant-text)', cursor: 'pointer' }} onClick={() => setHistoryStakeholder(stk)}>{name}</span>
                        {role && <span style={{ fontSize: 11, color: 'var(--globant-muted)' }}>{role}</span>}
                        {accName && <span style={{ fontSize: 11, color: 'var(--globant-muted)' }}>· {accName}</span>}
                        <span style={{ fontSize: 10, fontWeight: 700, color: chColor, padding: '2px 8px', borderRadius: 10, background: chBg, border: `1px solid ${chColor}55` }}>{step.channel}</span>
                        <span style={{ fontSize: 10, color: 'var(--globant-muted)', padding: '2px 8px', borderRadius: 10, background: 'rgba(30,42,44,0.04)', border: '1px solid var(--globant-border)' }}>Step {stepIdx + 1}/{totalSteps} · {campName}</span>
                      </div>
                      {step.note && <div style={{ fontSize: 11, color: 'var(--globant-muted)', fontStyle: 'italic', marginBottom: 6 }}>&#34;{step.note}&#34;</div>}
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {step.channel === 'WhatsApp' && phone && (
                          <a href={`https://wa.me/${phone.replace(/\D/g,'')}`} target="_blank" rel="noreferrer"
                            style={{ fontSize: 11, padding: '5px 12px', borderRadius: 6, background: 'rgba(37,211,102,0.15)', color: '#1E7B4F', border: '1px solid rgba(37,211,102,0.3)', textDecoration: 'none', fontWeight: 600 }}>
                            💬 Open WhatsApp
                          </a>
                        )}
                        {step.channel === 'LinkedIn' && linkedin && (
                          <a href={linkedin} target="_blank" rel="noreferrer"
                            style={{ fontSize: 11, padding: '5px 12px', borderRadius: 6, background: 'rgba(10,102,194,0.15)', color: '#0a66c2', border: '1px solid rgba(10,102,194,0.3)', textDecoration: 'none', fontWeight: 600 }}>
                            🔗 Open LinkedIn
                          </a>
                        )}
                        <button className="action-btn btn-ghost" style={{ fontSize: 11 }} onClick={() => setHistoryStakeholder(stk)}>Historial</button>
                        <button onClick={() => dismissManualStep(key)} style={{ fontSize: 11, padding: '5px 8px', borderRadius: 6, background: 'rgba(30,42,44,0.04)', border: '1px solid var(--globant-border)', color: 'var(--globant-muted)', cursor: 'pointer' }}>Hecho</button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {urgentActions.length > 0 && (
        <div className="card" style={{ borderLeft: '3px solid #B4362C', background: 'rgba(239,68,68,0.03)' }}>
          <div className="card-header">
            <h3 style={{ color: '#B4362C' }}>⚡ Urgent Actions ({urgentActions.length})</h3>
            <span style={{ fontSize: 11, color: 'var(--globant-muted)' }}>Próximos pasos sugeridos por la IA a partir de conversaciones recientes: empieza por estos</span>
          </div>
          <div>
            {urgentActions.map(task => {
              const urgency = F(task, 'Urgency') || 'Medium';
              const intent = F(task, 'Intent') || 'other';
              const intentBadge = INTENT_BADGE[intent] || INTENT_BADGE.other;
              const stkName = F(task, 'Stakeholder') || '';
              const accIds = linkedIds(task, 'Cuenta');
              const acc = accIds[0] ? accounts.find(a => a.id === accIds[0]) : null;
              const accName = acc ? F(acc, 'Account Name') : '';
              const taskText = F(task, 'Nombre de la Acción') || '';
              const stkRec = stakeholders.find(s => { const full = `${F(s,'Name')||''} ${F(s,'Last name')||''}`.trim(); return full && stkName && full.toLowerCase() === stkName.toLowerCase(); });
              const urgColor = urgency === 'High' ? '#B4362C' : urgency === 'Medium' ? '#9A6212' : '#56625F';
              const urgBg = urgency === 'High' ? 'rgba(239,68,68,0.08)' : urgency === 'Medium' ? 'rgba(251,191,36,0.08)' : 'rgba(156,163,175,0.06)';
              const isEditing = editingTaskId === task.id;
              const isBusy = taskBusyId === task.id;
              return (
                <div key={task.id} style={{ padding: '12px 14px', marginBottom: 8, borderRadius: 8, background: urgBg, borderLeft: `3px solid ${urgColor}`, opacity: isBusy ? 0.5 : 1, transition: 'opacity 0.2s' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, marginBottom: 8 }}>
                    <div style={{ flex: 1 }}>
                      {stkName && <span style={{ fontWeight: 700, fontSize: 13, color: stkRec ? 'var(--globant-green)' : 'var(--globant-text)', cursor: stkRec ? 'pointer' : 'default' }} onClick={() => stkRec && setHistoryStakeholder(stkRec)}>{stkName}</span>}
                      {accName && <span style={{ fontSize: 11, color: 'var(--globant-muted)', marginLeft: stkName ? 8 : 0 }}>{accName}</span>}
                    </div>
                    <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                      <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 10, background: intentBadge.bg, color: intentBadge.color }}>{intentBadge.label}</span>
                      <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 10, background: urgColor + '22', color: urgColor }}>{urgency === 'High' ? 'Urgencia alta' : urgency === 'Medium' ? 'Urgencia media' : 'Urgencia baja'}</span>
                    </div>
                  </div>
                  {isEditing ? (
                    <div style={{ marginBottom: 8 }}>
                      <textarea className="input-field" style={{ width: '100%', minHeight: 60, fontSize: 12, fontFamily: 'inherit', resize: 'vertical' }} value={editingTaskText} onChange={e => setEditingTaskText(e.target.value)} autoFocus />
                      <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                        <button className="action-btn btn-primary" style={{ fontSize: 10, padding: '4px 10px' }} onClick={() => saveTaskEdit(task)}>Guardar</button>
                        <button className="action-btn btn-ghost" style={{ fontSize: 10, padding: '4px 10px' }} onClick={() => { setEditingTaskId(null); setEditingTaskText(''); }}>Cancelar</button>
                      </div>
                    </div>
                  ) : (
                    <div style={{ fontSize: 13, color: 'var(--globant-text)', lineHeight: 1.5, marginBottom: 10, padding: '8px 10px', background: 'rgba(30,42,44,0.04)', borderRadius: 6, borderLeft: `2px solid ${urgColor}` }}>
                      <span style={{ fontSize: 9, fontWeight: 700, color: urgColor, letterSpacing: 1, textTransform: 'uppercase', display: 'block', marginBottom: 3 }}>Próximo paso</span>
                      {taskText || '—'}
                    </div>
                  )}
                  {!isEditing && (() => {
                    const stkEmail = stkRec ? F(stkRec, 'Email') : '';
                    const stkPhone = stkRec ? F(stkRec, 'Phone number') : '';
                    const stkLinkedin = stkRec ? F(stkRec, 'LinkedIn') : '';
                    return (
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                        {stkEmail && <button className="action-btn btn-email" style={{ fontSize: 10, padding: '5px 10px' }} disabled={isBusy} onClick={() => executeUrgentTask(task, 'Email')}>{isBusy ? 'Enviando…' : 'Enviar email y registrar'}</button>}
                        {stkPhone && <button className="action-btn btn-whatsapp" style={{ fontSize: 10, padding: '5px 10px' }} disabled={isBusy} onClick={() => executeUrgentTask(task, 'WhatsApp')}>{isBusy ? '⏳' : 'WhatsApp + log'}</button>}
                        {stkLinkedin && <button className="action-btn btn-linkedin" style={{ fontSize: 10, padding: '5px 10px' }} disabled={isBusy} onClick={() => executeUrgentTask(task, 'LinkedIn')}>{isBusy ? '⏳' : 'LinkedIn + log'}</button>}
                        {!stkRec && <span style={{ fontSize: 10, color: 'var(--globant-muted)', fontStyle: 'italic' }}>No se encontró el contacto: sáltalo o edítalo</span>}
                        <div style={{ width: 1, background: 'var(--globant-border)', margin: '0 4px', alignSelf: 'stretch' }} />
                        <button className="action-btn" style={{ fontSize: 10, padding: '5px 10px', background: 'rgba(251,191,36,0.12)', color: '#9A6212', border: '1px solid rgba(251,191,36,0.3)' }} disabled={isBusy} onClick={() => snoozeTask(task, 3)}>Posponer 3 días</button>
                        <button className="action-btn btn-ghost" style={{ fontSize: 10, padding: '5px 10px' }} disabled={isBusy} onClick={() => { setEditingTaskId(task.id); setEditingTaskText(taskText); }}>Editar</button>
                        <button className="action-btn btn-ghost" style={{ fontSize: 10, padding: '5px 10px' }} disabled={isBusy} onClick={() => markTaskDone(task)}>Saltar y marcar hecho</button>
                        {stkRec && <button className="action-btn btn-ghost" style={{ fontSize: 10, padding: '5px 10px' }} onClick={() => setHistoryStakeholder(stkRec)}>Ver conversación</button>}
                        {acc && goToAccount && <button className="action-btn btn-ghost" style={{ fontSize: 10, padding: '5px 10px' }} onClick={() => goToAccount(acc.id)}>Empresa</button>}
                      </div>
                    );
                  })()}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginBottom: 12, marginTop: 16 }}>
        <button className="action-btn btn-primary" style={{ fontSize: 12 }} onClick={() => setShowFuNewStk(!showFuNewStk)}>{showFuNewStk ? 'Cerrar' : 'Nuevo contacto'}</button>
      </div>

      {showFuNewStk && (
        <div className="card" style={{ borderLeft: '3px solid var(--globant-green)', marginBottom: 16 }}>
          <div className="card-header"><h3>Nuevo contacto</h3></div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10 }}>
            <div><label style={{ display: 'block', fontSize: 10, color: 'var(--globant-muted)', marginBottom: 3, fontWeight: 600 }}>NOMBRE *</label><input className="input-field" style={{ width: '100%', fontSize: 12, padding: '6px 8px' }} placeholder="ej. Camila" value={fuNewName} onChange={e => setFuNewName(e.target.value)} /></div>
            <div><label style={{ display: 'block', fontSize: 10, color: 'var(--globant-muted)', marginBottom: 3, fontWeight: 600 }}>APELLIDO</label><input className="input-field" style={{ width: '100%', fontSize: 12, padding: '6px 8px' }} placeholder="ej. Ortiz" value={fuNewLast} onChange={e => setFuNewLast(e.target.value)} /></div>
            <div><label style={{ display: 'block', fontSize: 10, color: 'var(--globant-muted)', marginBottom: 3, fontWeight: 600 }}>EMPRESA *</label><select className="input-field" style={{ width: '100%', fontSize: 12, padding: '6px 8px' }} value={fuNewAccountId} onChange={e => setFuNewAccountId(e.target.value)}><option value="">Elige una empresa…</option>{accounts.map(a => <option key={a.id} value={a.id}>{F(a, 'Account Name')}</option>)}</select></div>
            <div><label style={{ display: 'block', fontSize: 10, color: 'var(--globant-muted)', marginBottom: 3, fontWeight: 600 }}>CARGO</label><input className="input-field" style={{ width: '100%', fontSize: 12, padding: '6px 8px' }} placeholder="ej. Gerente comercial" value={fuNewRole} onChange={e => setFuNewRole(e.target.value)} /></div>
            <div><label style={{ display: 'block', fontSize: 10, color: 'var(--globant-muted)', marginBottom: 3, fontWeight: 600 }}>EMAIL</label><input className="input-field" type="email" style={{ width: '100%', fontSize: 12, padding: '6px 8px' }} placeholder="email@empresa.com" value={fuNewEmail} onChange={e => setFuNewEmail(e.target.value)} /></div>
            <div><label style={{ display: 'block', fontSize: 10, color: 'var(--globant-muted)', marginBottom: 3, fontWeight: 600 }}>TELÉFONO</label><input className="input-field" style={{ width: '100%', fontSize: 12, padding: '6px 8px' }} placeholder="+971..." value={fuNewPhone} onChange={e => setFuNewPhone(e.target.value)} /></div>
            <div><label style={{ display: 'block', fontSize: 10, color: 'var(--globant-muted)', marginBottom: 3, fontWeight: 600 }}>LINKEDIN</label><input className="input-field" style={{ width: '100%', fontSize: 12, padding: '6px 8px' }} placeholder="https://linkedin.com/in/..." value={fuNewLinkedin} onChange={e => setFuNewLinkedin(e.target.value)} /></div>
            <div><label style={{ display: 'block', fontSize: 10, color: 'var(--globant-muted)', marginBottom: 3, fontWeight: 600 }}>INFLUENCIA</label><select className="input-field" style={{ width: '100%', fontSize: 12, padding: '6px 8px' }} value={fuNewInfluence} onChange={e => setFuNewInfluence(e.target.value)}><option value="">Elige…</option><option value="High">Alta</option><option value="Medium">Media</option><option value="Low">Baja</option></select></div>
          </div>
          <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
            <button className="action-btn btn-primary" style={{ fontSize: 12 }} onClick={fuCreateStakeholder} disabled={!fuNewName.trim() || !fuNewAccountId || fuCreating}>{fuCreating ? 'Creando…' : 'Crear contacto'}</button>
            <button className="action-btn btn-ghost" style={{ fontSize: 12 }} onClick={() => setShowFuNewStk(false)}>Cancelar</button>
          </div>
        </div>
      )}

      {filtered.length > 0 && (
        <div className="card" style={{ borderLeft: '3px solid var(--globant-warning)' }}>
          <div className="card-header">
            <h3 style={{ color: 'var(--globant-warning)' }}>🟡 Needs First Contact ({filtered.length})</h3>
            <span style={{ fontSize: 11, color: 'var(--globant-muted)' }}>Nunca contactados: usa la IA o escríbeles directo</span>
          </div>
          <table className="data-table">
            <thead><tr><th>Contacto</th><th>Empresa</th><th>Influencia</th><th style={{ textAlign: 'center' }}>Hecho</th><th>Acciones</th></tr></thead>
            <tbody>{filtered.map(s => <StakeholderRow key={s.id} s={s} />)}</tbody>
          </table>
        </div>
      )}

      {responseModal && (
        <div className="modal-overlay" onClick={() => setResponseModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h3>Registrar respuesta</h3>
            <div style={{ fontSize: 13, color: 'var(--globant-muted)', marginBottom: 12 }}>{F(responseModal.stakeholder, 'Name')} responded — what did they say?</div>
            <div style={{ fontSize: 11, color: 'var(--globant-muted)', marginBottom: 12, padding: '6px 10px', background: 'rgba(30,42,44,0.03)', borderRadius: 6 }}>Your last message: {(F(responseModal.lastOutreach, 'Message') || '').substring(0, 100)}...</div>
            <textarea className="input-field" style={{ width: '100%', minHeight: 100, resize: 'vertical', marginBottom: 12, fontFamily: 'inherit' }} placeholder="Pega o escribe su respuesta…" value={responseText} onChange={e => setResponseText(e.target.value)} />
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="action-btn btn-ghost" style={{ flex: 1 }} onClick={() => setResponseModal(null)}>Cancelar</button>
              <button className="action-btn btn-primary" style={{ flex: 1 }} disabled={!responseText.trim() || logSaving} onClick={async () => { setLogSaving(true); setLogError(''); try { await logResponse(responseModal.stakeholder, responseText); setResponseModal(null); setResponseText(''); } catch (e) { setLogError(e.message); } setLogSaving(false); }}>{logSaving ? 'Guardando…' : logError ? 'Reintentar' : 'Registrar respuesta'}</button>
            </div>
            {logError && <div role="alert" style={{ fontSize: 12, color: '#B4362C', marginTop: 8 }}>{logError} El texto sigue acá para reintentar.</div>}
          </div>
        </div>
      )}

      {meetingModal && (() => {
        const ms = meetingModal.stakeholder;
        const msName = F(ms, 'Name') + (F(ms, 'Last name') ? ` ${F(ms, 'Last name')}` : '');
        const msEmail = F(ms, 'Email');
        const msAccNames = resolveLinked(ms, 'Account', accounts, 'Account Name');
        const msRole = F(ms, 'Role');
        const buildCalendarUrl = () => {
          if (!meetingDate) return '';
          const start = meetingTime ? `${meetingDate}T${meetingTime}:00` : `${meetingDate}T09:00:00`;
          const startDt = new Date(start);
          const endDt = new Date(startDt.getTime() + 30 * 60 * 1000);
          const fmt = (d) => d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
          const title = `${COMPANY_PROFILE.companyName} x ${msAccNames[0] || 'Account'} — ${msName}`;
          const details = `Meeting with ${msName} (${msRole}) at ${msAccNames.join(', ')}\n\n${meetingNotes || 'Intro call'}`;
          return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(title)}&dates=${fmt(startDt)}/${fmt(endDt)}&details=${encodeURIComponent(details)}${msEmail ? `&add=${encodeURIComponent(msEmail)}` : ''}`;
        };
        return (
          <div className="modal-overlay" onClick={() => setMeetingModal(null)}>
            <div className="modal" onClick={e => e.stopPropagation()}>
              <h3>Agendar reunión</h3>
              <div style={{ fontSize: 13, color: 'var(--globant-muted)', marginBottom: 4 }}>{msName} · {msRole}</div>
              <div style={{ fontSize: 12, color: 'var(--globant-accent)', marginBottom: 14 }}>{msAccNames.join(', ')}</div>
              <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
                <div style={{ flex: 1 }}><label style={{ display: 'block', fontSize: 11, color: 'var(--globant-muted)', marginBottom: 4, fontWeight: 600 }}>FECHA</label><input type="date" className="input-field" style={{ width: '100%' }} value={meetingDate} onChange={e => setMeetingDate(e.target.value)} /></div>
                <div style={{ flex: 1 }}><label style={{ display: 'block', fontSize: 11, color: 'var(--globant-muted)', marginBottom: 4, fontWeight: 600 }}>HORA</label><input type="time" className="input-field" style={{ width: '100%' }} value={meetingTime} onChange={e => setMeetingTime(e.target.value)} /></div>
              </div>
              <label style={{ display: 'block', fontSize: 11, color: 'var(--globant-muted)', marginBottom: 4, fontWeight: 600 }}>NOTAS / TEMARIO</label>
              <textarea className="input-field" style={{ width: '100%', minHeight: 70, resize: 'vertical', marginBottom: 14, fontFamily: 'inherit', fontSize: 12 }} placeholder="Tema, temario y preguntas clave…" value={meetingNotes} onChange={e => setMeetingNotes(e.target.value)} />
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="action-btn btn-ghost" style={{ flex: 1 }} onClick={() => setMeetingModal(null)}>Cancelar</button>
                {meetingDate && <button className="action-btn" style={{ flex: 1, background: 'rgba(66,133,244,0.15)', color: '#2D5F99', border: '1px solid rgba(66,133,244,0.3)' }} onClick={() => window.open(buildCalendarUrl(), '_blank')}>Abrir en el calendario</button>}
                <button className="action-btn" style={{ flex: 1, background: 'rgba(96,165,250,0.2)', color: '#2D5F99', border: '1px solid rgba(96,165,250,0.4)' }} disabled={logSaving} onClick={async () => { setLogSaving(true); setLogError(''); try { await logMeeting(ms, meetingNotes, meetingDate); setMeetingModal(null); setMeetingNotes(''); setMeetingDate(''); setMeetingTime(''); } catch (e) { setLogError(e.message); } setLogSaving(false); }}>{logSaving ? 'Guardando…' : logError ? 'Reintentar' : 'Registrar reunión'}</button>
              </div>
              {logError && <div role="alert" style={{ fontSize: 12, color: '#B4362C', marginTop: 8 }}>{logError} Lo que escribiste sigue acá para reintentar.</div>}
            </div>
          </div>
        );
      })()}

      {selectedStakeholder && (
        <AIMessageModal stakeholder={selectedStakeholder} onClose={() => setSelectedStakeholder(null)} onSend={useMessage}
          onSuccess={() => { setSelectedStakeholder(null); if (onLogActivity) onLogActivity(); }} data={data} />
      )}

      {composeEmail && (
        <EmailComposeModal stakeholder={composeEmail.stakeholder} initialSubject={composeEmail.subject} initialBody={composeEmail.body}
          replyContext={composeEmail.replyContext} accounts={accounts} outreach={outreach} gmailConnected={gmailConnectedFC}
          onClose={() => setComposeEmail(null)} onSuccess={() => { setComposeEmail(null); if (onLogActivity) onLogActivity(); }} />
      )}

      {historyStakeholder && (
        <StakeholderHistoryModal stakeholder={historyStakeholder} outreach={outreach} accounts={accounts}
          onClose={() => setHistoryStakeholder(null)} onRefresh={onLogActivity} onAddRecord={onAddRecord}
          allData={data} onNavigateToAccount={goToAccount} onSend={useMessage} />
      )}
    </div>
  );
}

// ============ EMAIL COMPOSE MODAL ============

export default FollowupCenter;
